import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Berichten.scss";
import commandBarStyles from "styles/commandBarStyles";
import {
  CommandBar,
  FontIcon,
  IColumn,
  ICommandBarItemProps,
  IconButton, IContextualMenuItem,
  TooltipHost
} from "@fluentui/react";
import { Berichtstatus } from "enums/berichtStatus";
import { useHistory, useParams } from "react-router-dom";
import DateRangeSelector from "components/DateRangeSelector/DateRangeSelector";
import { IBericht } from "../../../interfaces/berichten";
import { Zoeken } from "./Zoeken/Zoeken";
import { BerichtZoekTerm } from "./Zoeken/BerichtZoekTerm";
import { ZoekVelden } from "./Zoeken/ZoekVelden";
import BerichtBezorgingenTable from "./BerichtbezorgingenTable/BerichtbezorgingenTable";
import { getPropertyName } from "../../../lib/interfaceUtils";
import { stringToLocaleString } from "../../../lib/internationalization";
import {
  FilterDropdown,
  getDropdownOptions,
  iconStatusClass,
  statusFieldClass,
  statusIcon
} from "./BerichtbezorgingenTable/utils";
import { fetchBlob } from "../../../store/actions/blobs/data";
import StatusTabs from "./StatusTabs/StatusTabs";
import { useZod } from "../../../services/api/ApiClient";
import { useDateRangeFilter } from "components/DateRangeSelector/useDateRangeFilter";
import { useMsalInstance } from "../../../store/MsalContextProvider";

interface RouterParams {
  status: string;
}

interface BerichtBezorgingenProps {
  filterOpdracht?: boolean;
  filterStatus?: boolean;
  filterFactuur?: boolean;
  heading: string;
}

const Berichten: React.FC<BerichtBezorgingenProps> = ({ filterOpdracht, filterStatus, filterFactuur, heading }) => {
  const {zodiosHooks} = useZod();  
  const [refetch, setRefetch] = useState(false);
  const [statusFilter, setStatusFilter] = useState(Berichtstatus.Geen);
  const history = useHistory();
  const { status } = useParams<RouterParams>();
  const [zoekTermFilter, setZoekTermFilter] = useState<BerichtZoekTerm>({
    zoekTerm: "",
    veld: ZoekVelden.opdrachtnummer
  });
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { dateRange, dateFilterRange, onDateRangeChanged } = useDateRangeFilter();
  const {data: items, isLoading: isBerichtenLoading, mutate: fetchBerichten} = zodiosHooks.usePostApiabpBericht();
  const { data: aannemerInfo } = zodiosHooks.useGetApiabpInstellingenaannemerInfo();
  const msalContext = useMsalInstance();
  
  const handleResetFilters = () => {
    setFilters({});
  };

  // Luister naar de status die in de url staat bijv: /berichten/2, voor status 2
  // Zet de statusFilter op deze status
  useEffect(() => {
    const berichtStatus = status ? parseInt(status) : Berichtstatus.Geen;
    setStatusFilter(berichtStatus);
  }, [status]);

  useEffect(() => {
    if (refetch) {
      retrieveData();
      setRefetch(false);
    }
  }, [refetch]);

  useEffect(() => {
    if(!aannemerInfo) return;
    retrieveData();
  }, [dateFilterRange, aannemerInfo]);
  const retrieveData = () => {
    const request = {
      datumVan: dateRange.start.toISOString(),
      datumTm: dateRange.end.toISOString(),      
      contractorGln: aannemerInfo?.glnCode ?? null
    };
    fetchBerichten({...request});
  };

  const filteredItems = useMemo(() => {
    if(!items) return [];
    return items!
      .filter(item => (statusFilter === 0 || item.statusId === statusFilter))
      .filter((item) => !filters["status"] || item.status === filters["status"])
      .filter((item) => !filters["opdrachtStatus"] || item.opdrachtStatus === filters["opdrachtStatus"])
      .filter((item) => !filters["werkwijze"] || item.werkwijze === filters["werkwijze"])
      .filter((item) => !filters["soort"] || item.soort === filters["soort"])
      .filter((item) => !filters["van"] || item.van === filters["van"])
      .filter((item) => !filters["naar"] || item.naar === filters["naar"])
      .filter((item) => {
        if (!zoekTermFilter?.zoekTerm?.trim()) return true;
        const term = zoekTermFilter.zoekTerm.trim().toLowerCase();
        return zoekTermFilter.veld === ZoekVelden.opdrachtnummer
          ? item.opdracht?.toLowerCase().includes(term)
          : true;
      });
  }, [items, filters, zoekTermFilter, statusFilter]);


  const handleBlobDownload = useCallback(
    (blobId: string | undefined, soort: "ontvangen-bericht" | "response-ontvangst" | "bezorgd-bericht" | "response-bezorging") => {
      if (!blobId) return;      
      const fileName = `${soort}-${blobId}.xml`;
      fetchBlob({ id: blobId, fileName },msalContext).then();
    },
    []
  );  

  const commandBarItems: ICommandBarItemProps[] = useMemo(
    () => [
      {
        key: "pagetitle",
        onRender: () => (
          <span className="page-title">{heading}</span>
        )
      }
    ],
    []
  );

  const commandBarItemsFar: ICommandBarItemProps[] = useMemo(
    () => [
      {
        key: "resetFilters",
        text: "Alle filters wissen",
        iconProps: { iconName: "Clear", className: "icon" },
        split: false,
        ariaLabel: "Alle filters wissen",
        onClick: handleResetFilters,
        className: "refresh-button"
      },
      {
        key: "refresh",
        text: "Vernieuwen",
        iconProps: { iconName: "Refresh", className: "icon" },
        split: false,
        ariaLabel: "Vernieuwen",
        onClick: () => setRefetch(true),
        className: "refresh-button"
      }
    ],
    [setRefetch]
  );

  function onStatusFilterChanged(filter: string) {
    handleResetFilters();
    if (filter === "0") {
      history.push(`/dico`);
    } else {
      history.push(`/dico/${filter}`);
    }
  }

  const handleFilterChange = (filterKey: string, selectedKey: string | undefined) => {
    setFilters((prevFilters) => {
      if (selectedKey === "0") {
        return {
          ...prevFilters,
          [filterKey]: ""
        };
      }

      return {
        ...prevFilters,
        [filterKey]: selectedKey
      };
    });
  };
  
  const opdrachtStatusOptions = useMemo(() => {
       return getDropdownOptions(items, "opdrachtStatus")
      }, [items]
  );
  
  const berichtBerzogingOptions = (item: IBericht) => {
    var menuItems: IContextualMenuItem[] = [
      {
        key: "berichtinfo",
        text: `Bericht: ${item.berichtId.toString()}-${item?.bezorgingId?.toString()}`,
        disabled: true
      },
      {
        key: "httpResponseCode",
        text: item.bezorgingHttpStatusCode ? `HTTP response status code bezorging: ${item?.bezorgingHttpStatusCode}` : "",
        disabled: true
      }
    ];
    switch(item.soort){
      case "Opdracht":
        menuItems.splice(1,0,{
          key: "downloadbezorgdbericht",
          text: "Bezorgd bericht",
          iconProps: { iconName: "Download" },
          disabled: !item.bezorgdBerichtId,
          onClick: () => handleBlobDownload(item?.bezorgdBerichtId ?? undefined, "bezorgd-bericht")
        },{
          key: "downloadbezordResponse",
          text: "Response bezorging",
          iconProps: { iconName: "Download" },
          disabled: !item.bezorgdBerichtResponseId,
          onClick: () => handleBlobDownload(item?.bezorgdBerichtResponseId ?? undefined, "response-bezorging")
        });
        break;
      case "Factuur":
      case "Status":
        menuItems.splice(1,0,{
            key: "downloadontvangstbericht",
            text: "Ontvangen bericht",
            iconProps: { iconName: "Download" },
            disabled: !item.ontvangenBerichtId,
            onClick: () => handleBlobDownload(item?.ontvangenBerichtId ?? undefined, "ontvangen-bericht")
          },
          {
            key: "downloadontvangstResponse",
            text: "Response ontvangst",
            iconProps: { iconName: "Download" },
            disabled: !item.ontvangenBerichtResponseId,
            onClick: () => handleBlobDownload(item?.ontvangenBerichtResponseId ?? undefined, "response-ontvangst")
          });
        break;
    }
    return {
      items: menuItems
    };
  };
  
  const werkwijzeOptions = useMemo(() => getDropdownOptions(items, "werkwijze"), [items]);
  const soortOptions = useMemo(() => getDropdownOptions(items, "soort"), [items]);
  const statusOptions = useMemo(() => getDropdownOptions(items, "status"), [items]);
  const vanOptions = useMemo(() => getDropdownOptions(items, "van"), [items]);
  const naarOptions = useMemo(() => getDropdownOptions(items, "naar"), [items]);

  const columns: IColumn[] = useMemo(() =>
      [
        {
          key: "iconStatus",
          name: "Status icon",
          className: "status-field",
          iconClassName: "header-status-field-icon",
          iconName: "Page",
          isIconOnly: true,
          minWidth: 24,
          maxWidth: 24,
          onRender: (item: IBericht) => (
            <div className={statusFieldClass(item.statusId)}><FontIcon aria-label="status icon"
                                                                       iconName={statusIcon(item.statusId)}
                                                                       className={iconStatusClass} /></div>
          )
        },
        {
          key: getPropertyName<IBericht>("ontvangen"),
          name: "Ontvangen",
          fieldName: getPropertyName<IBericht>("ontvangen"),
          minWidth: 80,
          maxWidth: 100,
          isResizable: true,
          data: "date",
          onRender: (item: IBericht) => (
            <span>{item.ontvangen && stringToLocaleString(item.ontvangen.toString())}</span>
          )
        },
        {
          key: getPropertyName<IBericht>("opdracht"),
          name: "Opdrachtnummer",
          fieldName: getPropertyName<IBericht>("opdracht"),
          minWidth: 100,
          maxWidth: 140,
          isResizable: true,
          data: "string"
        },
        {
          key: getPropertyName<IBericht>("opdrachtStatus"),
          name: "Opdrachtstatus",
          fieldName: getPropertyName<IBericht>("opdrachtStatus"),
          minWidth: 160,
          maxWidth: 180,
          isResizable: true,
          data: "string",
          onRenderHeader: () => (
            <FilterDropdown
              filterKey="opdrachtStatus"
              placeholder="Opdrachtstatus"
              selectedKey={filters["opdrachtStatus"]}
              options={opdrachtStatusOptions}
              onFilterChange={handleFilterChange}
            />
          )
        },
        {
          key: getPropertyName<IBericht>("soort"),
          name: "Soort",
          fieldName: getPropertyName<IBericht>("soort"),
          minWidth: 150,
          maxWidth: 160,
          isResizable: true,
          data: "string",
          onRenderHeader: () => (
            <FilterDropdown
              filterKey="soort"
              placeholder="Soort"
              selectedKey={filters["soort"]}
              options={soortOptions}
              onFilterChange={handleFilterChange}
            />
          )
        },
        {
          key: getPropertyName<IBericht>("werkwijze"),
          name: "Werkwijze",
          fieldName: getPropertyName<IBericht>("werkwijze"),
          minWidth: 150,
          maxWidth: 160,
          isResizable: true,
          data: "string",
          onRenderHeader: () => (
            <FilterDropdown
              filterKey="werkwijze"
              placeholder="Werkwijze"
              selectedKey={filters["werkwijze"]}
              options={werkwijzeOptions}
              onFilterChange={handleFilterChange}
            />
          )
        },
        {
          key: getPropertyName<IBericht>("status"),
          name: "Status",
          fieldName: getPropertyName<IBericht>("status"),
          minWidth: 160,
          maxWidth: 150,
          isResizable: true,
          data: "string",
          onRenderHeader: () => (
            <FilterDropdown
              filterKey="status"
              placeholder="Status"
              selectedKey={filters["status"]}
              options={statusOptions}
              onFilterChange={handleFilterChange}
            />
          )
        },
        {
          key: getPropertyName<IBericht>("laatsteMutatie"),
          name: "Laatste mutatie",
          fieldName: getPropertyName<IBericht>("laatsteMutatie"),
          minWidth: 110,
          maxWidth: 130,
          isResizable: true,
          data: "date",
          onRender: (item: IBericht) => (
            <span>{item.laatsteMutatie && stringToLocaleString(item.laatsteMutatie.toString())}</span>
          )
        },
        {
          key: getPropertyName<IBericht>("van"),
          name: "Van",
          fieldName: getPropertyName<IBericht>("van"),
          minWidth: 150,
          maxWidth: 160,
          isResizable: true,
          data: "string",
          onRenderHeader: () => (
            <FilterDropdown
              filterKey="van"
              placeholder="Van"
              selectedKey={filters["van"]}
              options={vanOptions}
              onFilterChange={handleFilterChange}
            />
          )
        },
        {
          key: getPropertyName<IBericht>("naar"),
          name: "Naar",
          fieldName: getPropertyName<IBericht>("naar"),
          minWidth: 180,
          maxWidth: 200,
          isResizable: true,
          data: "string",          
          onRenderHeader: () => (
            <FilterDropdown
              filterKey="naar"
              placeholder="Naar"
              selectedKey={filters["naar"]}
              options={naarOptions}
              onFilterChange={handleFilterChange}
            />
          )
        },
        {
          key: getPropertyName<IBericht>("toelichting"),
          name: "Toelichting",
          fieldName: getPropertyName<IBericht>("toelichting"),
          minWidth: 80,
          maxWidth: 600,
          isResizable: true,
          data: "string",
          onRender: (item: IBericht) => {
            return (
              <TooltipHost
                content={item.toelichting ?? ""}
                id="tooltip-toelichting"
              >
                <span>{item.toelichting}</span>
              </TooltipHost>
            );
          }
        },
        {
          key: "origineelbericht",
          name: "",
          minWidth: 20,
          maxWidth: 20,
          isResizable: false,
          onRender: (item: IBericht) => (
            <div style={{ display: "flex", justifyContent: "center", alignContent: "center", height: "auto" }}>
              <IconButton
                styles={{
                  root: { height: "auto", ":active": { background: "#ffffff" } },
                  splitButtonContainer: { width: "auto" }
                }}
                iconProps={{ iconName: "MoreVertical" }}
                menuProps={berichtBerzogingOptions(item)}
                hidden={true}
                menuIconProps={{ iconName: "" }}
              />
            </div>
          )
        }
      ],
    [items]);

  return (
    <div className="berichtbezorgingen">
      <div className="berichtbezorgingen-header">
        <CommandBar items={commandBarItems} farItems={commandBarItemsFar} styles={commandBarStyles} />
        <div className="berichtbezorgingen-header-actions">
          <div className="berichtbezorgingen-header-actions-top flex-container flex-gap-16">
            <DateRangeSelector dateRange={dateRange} onDateRangeChange={onDateRangeChanged}></DateRangeSelector>
            <Zoeken zoekTerm={zoekTermFilter}
                    onZoekTermChanged={zoekTermChanged => setZoekTermFilter(zoekTermChanged)}></Zoeken>
          </div>
          <div className="berichtbezorgingen-header-actions-bottom">
            <StatusTabs statusFilter={statusFilter} items={items}
                        onStatusFilterChanged={onStatusFilterChanged} filteredItems={filteredItems}></StatusTabs>
          </div>
        </div>
      </div>
      <div className="berichtbezorgingen-content">
        <BerichtBezorgingenTable items={filteredItems}
                                 loading={isBerichtenLoading}
                                 columns={columns}
        />
      </div>
      
    </div>
  );
};

export default Berichten;